/**
 * Typed fetch wrapper for the NestJS API.
 *
 * Responsibilities: base URL from env, Bearer-token injection (from auth-token),
 * JSON headers, a request timeout, and normalized errors. HTTP failures throw
 * `ApiException`; unreachable/timed-out requests throw `NetworkError` so feature
 * hooks can fall back to mock data. Tokens are never read from web storage.
 */

import { ApiException, NetworkError, type ApiErrorBody } from "./api-errors";
import { getAccessToken } from "./auth-token";

export const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "http://localhost:3001/api/v1";
const DEFAULT_TIMEOUT_MS = 15_000;

/** Standard list envelope used by paginated endpoints (docs/api.md §1). */
export interface PaginatedResponse<T> {
  data: T[];
  meta: { page: number; limit: number; total: number };
}

export type QueryParams = Record<string, string | number | boolean | undefined | null>;
type JsonBody = Record<string, unknown> | unknown[];

interface RequestOptions {
  params?: QueryParams;
  body?: JsonBody;
  signal?: AbortSignal;
  timeoutMs?: number;
}

function buildUrl(path: string, params?: QueryParams): string {
  const url = `${BASE_URL}${path}`;
  if (!params) return url;
  const qs = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    qs.append(key, String(value));
  }
  const query = qs.toString();
  return query ? `${url}?${query}` : url;
}

async function request<T>(method: string, path: string, opts: RequestOptions = {}): Promise<T> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), opts.timeoutMs ?? DEFAULT_TIMEOUT_MS);
  if (opts.signal) {
    if (opts.signal.aborted) controller.abort();
    else opts.signal.addEventListener("abort", () => controller.abort(), { once: true });
  }

  const token = getAccessToken();
  const hasBody = opts.body !== undefined;

  let res: Response;
  try {
    res = await fetch(buildUrl(path, opts.params), {
      method,
      signal: controller.signal,
      headers: {
        Accept: "application/json",
        ...(hasBody ? { "Content-Type": "application/json" } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: hasBody ? JSON.stringify(opts.body) : undefined,
    });
  } catch {
    // fetch rejects on network failure or on abort (our timeout).
    throw new NetworkError(controller.signal.aborted ? "Délai dépassé : API injoignable" : "API injoignable");
  } finally {
    clearTimeout(timer);
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text().catch(() => "");
  let parsed: unknown = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = null;
    }
  }

  if (!res.ok) {
    const errBody = (parsed as { error?: ApiErrorBody } | null)?.error;
    throw new ApiException(res.status, errBody ?? { code: "UNKNOWN", message: res.statusText || "Erreur" });
  }
  return parsed as T;
}

export const apiGet = <T>(path: string, params?: QueryParams, opts?: Omit<RequestOptions, "params" | "body">): Promise<T> =>
  request<T>("GET", path, { ...opts, params });

export const apiPost = <T>(path: string, body?: JsonBody, opts?: Omit<RequestOptions, "body">): Promise<T> =>
  request<T>("POST", path, { ...opts, body });

export const apiPatch = <T>(path: string, body?: JsonBody, opts?: Omit<RequestOptions, "body">): Promise<T> =>
  request<T>("PATCH", path, { ...opts, body });

export const apiPut = <T>(path: string, body?: JsonBody, opts?: Omit<RequestOptions, "body">): Promise<T> =>
  request<T>("PUT", path, { ...opts, body });

export const apiDelete = <T>(path: string, opts?: RequestOptions): Promise<T> =>
  request<T>("DELETE", path, opts ?? {});

// Re-exported for backward compatibility with earlier imports.
export { ApiException } from "./api-errors";
